import { ApiBase, HttpMethod } from "../core/api-base";
import { z } from "zod";
import { GetPostDetail200Response } from "../api/generated/posts.zod";
import { AddReadToPost201Response } from "../api/generated/reads.zod";
import { TaskScheduler } from "../core/task-scheduler";
import { SNSPreviewManagerSetup } from "../sns/preview-manager/sns-preview-manager-setup";
import { perfLogger } from "../core/perf-logger";
import type { ModalPostData, ModalReadPayload } from "./modal-render";
import { ModalForm } from "./modal-form";

type ModalPostResponse = z.infer<typeof GetPostDetail200Response>;
type ModalReadResponse = z.infer<typeof AddReadToPost201Response>;

export class ModalApiService extends ApiBase {
    private static readonly modalForm = new ModalForm();

    public static async fetchPostDetailDocument(postId: number, boardId: string): Promise<Document | null> {
        if (!Number.isInteger(postId) || postId <= 0 || !boardId) {
            throw new Error("Post ID and Board ID are required");
        }

        // XHR: GET /posts/{boardId}/posts/{postId} — modal HTML fragment (web route, ajax-only)
        const response = await super.apiRequest(`/posts/${boardId}/posts/${String(postId)}`, HttpMethod.Get, null, {
            noSpinner: true,
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch post detail HTML: ${response.status}`);
        }

        const html = await response.text();
        if (!html.trim()) {
            return null;
        }

        const parser = new DOMParser();
        return parser.parseFromString(html, "text/html");
    }

    public static async fetchCreateModalFragment(boardId: string, postProductId?: string): Promise<Document | null> {
        const params = new URLSearchParams();
        if (postProductId) params.set("post_product_id", postProductId);
        const qs = params.toString();
        const url = `/posts/${encodeURIComponent(boardId)}/create-modal${qs ? `?${qs}` : ""}`;

        const response = await super.apiRequest(url, HttpMethod.Get, null, { noSpinner: true });

        if (!response.ok) {
            throw new Error(`Failed to fetch create modal fragment: ${response.status}`);
        }

        const html = await response.text();
        if (!html.trim()) {
            return null;
        }

        return new DOMParser().parseFromString(html, "text/html");
    }

    public static async fetchPostEditFormDocument(postId: number, boardId: string): Promise<Document | null> {
        if (!Number.isInteger(postId) || postId <= 0 || !boardId) {
            throw new Error("Post ID and Board ID are required");
        }

        // XHR: GET /posts/{boardId}/posts/{postId}/edit — edit form HTML fragment (web route, ajax-only)
        const response = await super.apiRequest(`/posts/${boardId}/posts/${String(postId)}/edit`, HttpMethod.Get, null, {
            noSpinner: true,
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch post edit form: ${response.status}`);
        }

        const html = await response.text();
        if (!html.trim()) {
            return null;
        }

        return new DOMParser().parseFromString(html, "text/html");
    }

    public static async fetchPostData(postId: number, boardId: string): Promise<ModalPostData> {
        if (!Number.isInteger(postId) || postId <= 0 || !boardId) {
            throw new Error("Post ID and Board ID are required");
        }

        // XHR: GET /api/v1/posts/{boardId}/{postId} — post JSON (YAML-defined API)
        const response = await super.apiRequest(`/api/v1/posts/${boardId}/${String(postId)}`, HttpMethod.Get, null, {
            noSpinner: true,
        });

        if (!response.ok) {
            throw new Error(`Failed to fetch post data: ${response.status}`);
        }

        const data = (await response.json()) as ModalPostResponse;

        if (data.data?.item) {
            return data.data.item as ModalPostData;
        }

        console.error("Unexpected API response structure:", data);
        throw new Error("Invalid API response");
    }

    public static async markAsRead(postId: number, boardId: string): Promise<ModalReadPayload> {
        if (!Number.isInteger(postId) || postId <= 0 || !boardId) {
            throw new Error("Post ID and Board ID are required");
        }

        // XHR: POST /api/v1/posts/{boardId}/{postId}/reads — mark post as read (YAML-defined API)
        const response = await super.apiPost(`/api/v1/posts/${boardId}/${String(postId)}/reads`, null, {
            noSpinner: true,
        });

        if (!response.ok) {
            throw new Error(`Failed to mark post as read: ${response.status}`);
        }

        const data = (await response.json()) as ModalReadResponse;
        if (data.success && typeof data.reads_count !== "undefined") {
            return {
                reads_count: data.reads_count,
                post_id: data.read?.post_id,
                board_id: data.read?.board_id,
            };
        }

        throw new Error("Invalid API response");
    }

    public static processSNSPreviews(modalBody: HTMLElement | null): void {
        const perfSession = perfLogger.activeSession();
        perfSession?.mark("snsPreview:scheduled");

        const snsPreviewManager = SNSPreviewManagerSetup.getManager();
        if (!TaskScheduler || !snsPreviewManager) {
            return;
        }

        TaskScheduler.scheduleDebounced(
            () => {
                if (!modalBody) return;

                const t0 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
                perfSession?.mark("snsPreview:begin");

                const contentElement = this.modalForm.getPostDetailContent(modalBody) || modalBody;

                if (contentElement && contentElement !== modalBody) {
                    snsPreviewManager.processContentForDisplay(contentElement, false);
                } else if (contentElement === modalBody) {
                    snsPreviewManager.processContentForDisplay(contentElement, false);
                }

                const t1 = typeof performance !== "undefined" && performance.now ? performance.now() : Date.now();
                perfSession?.mark("snsPreview:done", {
                    ms: Math.round((t1 - t0) * 1000) / 1000,
                });
            },
            50,
            "element-modal-sns-preview",
        );
    }
}
